import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useAddAppInvitationMutation,
  useDeleteAppInvitationMutation,
  useLazyFetchAppInvitationsQuery,
  useResendAppInvitationMutation,
} from '../api/app-invitation.api';
import {
  AppInvitationAC,
  setAppInvitationCurrentPage,
  setIsAppInvitationModalOpen,
} from '../store/app-invitation.slice';
import {
  AddAppInvitationPayload,
  DeleteAppInvitationPayload,
  FetchAppInvitationsPayload,
  IAppInvitation,
  ResendAppInvitationPayload,
} from '../types/app-invitation.types';

interface UseAppInvitation {
  fetchAppInvitationsHandler: (payload: FetchAppInvitationsPayload) => Promise<void>;
  addAppInvitationHandler: (payload: AddAppInvitationPayload) => Promise<IAppInvitation | null>;
  resendAppInvitationHandler: (payload: ResendAppInvitationPayload) => Promise<void>;
  deleteAppInvitationHandler: (payload: DeleteAppInvitationPayload) => Promise<void>;
  openAppInvitationModal: () => void;
  closeAppInvitationModal: () => void;
  clearAppInvitationState: () => void;
}

export const useAppInvitation = (): UseAppInvitation => {
  const dispatch = useAppDispatch();

  const [fetchAppInvitationsQuery] = useLazyFetchAppInvitationsQuery();
  const [addAppInvitationQuery] = useAddAppInvitationMutation();
  const [resendAppInvitationQuery] = useResendAppInvitationMutation();
  const [deleteAppInvitationQuery] = useDeleteAppInvitationMutation();

  const addAppInvitationHandler = async (payload: AddAppInvitationPayload) => {
    dispatch(AppInvitationAC.toggleLoading(true));
    const { data: response, error } = await addAppInvitationQuery(payload);
    dispatch(AppInvitationAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to create invitation!');
      return null;
    }

    if (payload.email) {
      toast.success(`Invitation for ${payload.email} created successfully!`);
    } else {
      toast.success('Invitation created successfully!');
    }

    dispatch(AppInvitationAC.addItem(response.data));
    return response.data;
  };

  const resendAppInvitationHandler = async (payload: ResendAppInvitationPayload) => {
    dispatch(AppInvitationAC.toggleLoading(true));
    const { error } = await resendAppInvitationQuery(payload);
    dispatch(AppInvitationAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to resend invitation!');
      return;
    }
    toast.success('Invitation resent successfully!');
  };

  const deleteAppInvitationHandler = async (payload: DeleteAppInvitationPayload) => {
    dispatch(AppInvitationAC.toggleLoading(true));
    const { error } = await deleteAppInvitationQuery(payload);
    dispatch(AppInvitationAC.toggleLoading(false));
    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to delete invitation!');
      return;
    }
    dispatch(AppInvitationAC.removeItem(payload.id));
    toast.success('Invitation deleted successfully!');
  };

  const fetchAppInvitationsHandler = async (payload: FetchAppInvitationsPayload) => {
    dispatch(AppInvitationAC.toggleLoading(true));
    const { data: response, error } = await fetchAppInvitationsQuery(payload);
    dispatch(AppInvitationAC.toggleLoading(false));
    if (error || !response?.data) {
      toast.error(getApiErrorMessage(error) || 'Failed to fetch invitations!');
      return;
    }
    batch(() => {
      dispatch(AppInvitationAC.addItems(response.data));
      dispatch(AppInvitationAC.setPaginationMeta(response.meta));
    });
  };

  const openAppInvitationModal = () => {
    dispatch(setIsAppInvitationModalOpen(true));
  };

  const clearAppInvitationState = () => {
    batch(() => {
      dispatch(AppInvitationAC.clearItems());
      dispatch(AppInvitationAC.clearFilters());
      dispatch(AppInvitationAC.clearSorts());
      dispatch(setAppInvitationCurrentPage(1));
    });
  };

  const closeAppInvitationModal = () => {
    dispatch(setIsAppInvitationModalOpen(false));
    clearAppInvitationState();
  };

  return {
    clearAppInvitationState,
    fetchAppInvitationsHandler,
    openAppInvitationModal,
    closeAppInvitationModal,
    addAppInvitationHandler,
    resendAppInvitationHandler,
    deleteAppInvitationHandler,
  };
};
